import { z } from 'zod';

const stringifyPath = (path: (number | string)[]): string => path.join('.');

/**
 * This schema is a duplicate of the schema defined in '@craft/lib/schemas' but is repeated here
 * to avoid adding @craft/lib dependencies in the environment module which needs to be edge
 * safe.
 */
export const StringBooleanSchema = z.union([z.string(), z.boolean()]).transform(val => {
  if (typeof val === 'string') {
    return val.toLowerCase() === 'true' ? true : val.toLowerCase() === 'false' ? false : undefined;
  }
  return val;
});

/**
 * Sanitizes the provided string value, {@link string | null}, returning null if the
 * trimmed string is empty (or null is explicitly provided).
 *
 * Note: This function is duplicated from '@craft/lib/primitives' to avoid adding @craft/lib
 * dependencies in the environment module which needs to be edge safe.
 *
 * @param {null | string} value
 *   The potentially nullish string value to sanitize.
 *
 * @returns {null | string}
 *   The sanitized string value, which can be null or the sanitized, trimmed string.
 */
function sanitizeStringValue(value: null | string): null | string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return value;
}

/**
 * A Zod schema that trims a required string value and transforms an empty (or whitespace-only)
 * result to {@link null} rather than rejecting it.
 *
 * This schema is duplicated from '@craft/lib/schemas' to avoid adding @craft/lib dependencies in
 * the environment module which needs to be edge safe.
 */
export const SanitizedStringSchema = z
  .string()
  .trim()
  .transform(value => sanitizeStringValue(value));

/**
 * A schema that represents a string environment variable that is optional, but if it is present,
 * it must not be an empty string.  If the variable is present but is an empty string, a warning
 * will be logged, and the variable will be treated as if it were not present (i.e. undefined).
 */
export const OptionalStringVariableSchema = z
  .string()
  .trim()
  .optional()
  .transform((value, ctx) => {
    if (value === undefined) {
      return undefined;
    }
    if (value.length === 0) {
      /* eslint-disable-next-line no-console -- The logger depends on the environment module so
         the environment module must avoid logger usage. */
      console.warn(
        `The environment variable at path '${stringifyPath(ctx.path)}' was present but is an ` +
          'empty string!  If the variable is optional, it will be excluded, and if the ' +
          'variable is not optional, it will cause the schema validation to fail.',
        { path: ctx.path, value },
      );
      return undefined;
    }
    return value.trim();
  });

export const RequiredStringVariableSchema = z.string().trim().min(1);

/**
 * A schema that represents an optional string environment variable whose parsed value must be
 * serializable across the NextJS server/client props boundary.
 *
 * NextJS cannot serialize `undefined` in the JSON produced by `getServerSideProps`, so any
 * environment value injected into page props must normalize absence to {@link null}. This schema
 * accepts the same inputs as {@link OptionalStringVariableSchema} (additionally tolerating an
 * explicit {@link null}, which appears when a previously serialized environment is re-parsed) and
 * transforms an absent value to {@link null} instead of `undefined`.
 */
export const SerializableOptionalStringVariableSchema =
  OptionalStringVariableSchema.nullable().transform(value => value ?? null);
